import React, { useCallback, useState } from 'react';

import { Divider } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { Keyboard } from 'react-native';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { toaster } from 'components/toaster/toaster';
import { Typography, withTypography } from 'components/ui';
import useErrorModal from 'hooks/use-error-modal';
import { t, tRich } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { useUIContext } from 'state/ui';
import { spacing } from 'styles/constants';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import { usePrepaidByNumber } from '../../../hooks/prepaid';
import { useCardNumber } from '../../../hooks/use-giftcard-number';
import { usePaymentContext } from '../../../state';
import { AddGiftCardInput } from '../../add-gift-card-input';
import { PaymentModalFocusTrap } from '../../payment-modal-focus-trap';
import GiftCardImage from '../gift-card-image';

const GIFT_CARD_NUMBER_TRANSLATION_ID = 'giftCardNumber';

export const TransferGiftCardModal: React.FC = () => {
  const { getPrepaidPaymentMethod, mergePrepaidCardBalances } = usePaymentContext();
  const { formatCurrencyForUser } = useUIContext();
  const router = useRouter();

  const [transferInfo, setTransferInfo] = useState<{
    transferCardBalance?: number;
    cardNumber?: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const {
    giftCardNumber,
    errors,
    isValidGiftCardNumber,
    handleCardNumberChange,
    validateCardNumber,
  } = useCardNumber();
  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });
  const { getPrepaidByNumber } = usePrepaidByNumber();
  const handleGiftCardNumberChange = useCallback(
    (cardNumber: string) => {
      handleCardNumberChange(cardNumber);
      validateCardNumber(cardNumber);
    },
    [handleCardNumberChange, validateCardNumber]
  );

  const showGiftCardAddingErrorModal = useCallback(
    (error: unknown) => {
      openErrorDialog({ message: t('giftCardAddingError'), error: toError(error) });
      logger.error({ error, message: 'Error transfer gift card' });
    },
    [openErrorDialog]
  );

  const transferCard = useCallback(
    async (event: any) => {
      Keyboard.dismiss();
      event.preventDefault();
      setIsLoading(true);
      try {
        const { data, errors } = await getPrepaidByNumber({ cardNumber: giftCardNumber });
        if (!data) {
          showGiftCardAddingErrorModal(errors);
        } else {
          const transferCardBalance = data?.balance;
          setTransferInfo({ transferCardBalance, cardNumber: giftCardNumber });
        }
      } catch (error) {
        showGiftCardAddingErrorModal(error);
      } finally {
        setIsLoading(false);
      }
    },
    [getPrepaidByNumber, giftCardNumber, showGiftCardAddingErrorModal]
  );

  const confirmTransferCard = async () => {
    setIsLoading(true);
    try {
      const newBalance = await mergePrepaidCardBalances({
        sourceCardNumber: cardNumber || '',
      });

      logEvent(CustomEventNames.GIFT_CARD_BALANCE_TRANSFER, EventTypes.Other, {
        'Current Balance': newBalance,
      });

      toaster.positive(t('transferBalanceCompletedMessage'));

      router.dismiss();
    } catch (error) {
      openErrorDialog({ message: t('giftCardAddingError'), error: toError(error) });
      logger.error({ error, message: 'Error transfer gift card' });
    } finally {
      setIsLoading(false);
    }
  };

  // part 1 - enter info
  if (!transferInfo) {
    const modalTitle = t('transferBalanceFromPrepaidCard', { cardAlias: t('cardAlias.bk') });

    return (
      <PaymentModalFocusTrap label={modalTitle}>
        <CardModal
          headerText={modalTitle}
          testID="transfer-giftcard-form"
          analyticsEvent="Transfer gift card"
          standardPadding
          keyboardAwareFooter
          footer={
            <ActionButton
              fullWidth
              testID="transfer-now"
              disabled={!isValidGiftCardNumber}
              onPress={transferCard}
              isLoading={isLoading}
            >
              {t('transfer')}
            </ActionButton>
          }
        >
          <GiftCardImage />
          <AddGiftCardInput
            onChange={handleGiftCardNumberChange}
            errorMessage={errors?.giftCardNumber}
            label={t(GIFT_CARD_NUMBER_TRANSLATION_ID as TranslationKey)}
            testID="transfer-giftcard-input"
          />
          <ErrorDialog />
        </CardModal>
      </PaymentModalFocusTrap>
    );
  }

  // part 2 - confirm (has transferInfo filled in)
  const { transferCardBalance, cardNumber } = transferInfo;

  const currentBalance = getPrepaidPaymentMethod()?.prepaid?.currentBalance;
  const formattedCurrecyBalance = formatCurrencyForUser(currentBalance);
  const formattedTransferCardBalance = formatCurrencyForUser(transferCardBalance);
  const formatCardNumber = cardNumber?.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ');

  const cardAlias = t('cardAlias.bk');
  const isTransferCardBalanceValid = transferCardBalance && transferCardBalance > 0;
  const { actionDetails, actionHandler, actionText } = isTransferCardBalanceValid
    ? {
        actionDetails: t('wouldYouLikeToAddToPrepaidCard', {
          amount: formattedTransferCardBalance,
          cardAlias,
        }),
        actionText: t('yesTransfer', { amount: formattedTransferCardBalance }),
        actionHandler: confirmTransferCard,
      }
    : {
        actionDetails: t('wouldYouLikeToAddADifferentCard', { cardAlias }),
        actionText: t('yesAddNewCard', { cardAlias }),
        actionHandler: () => {
          //navigate(-1) go back to transfer-gift-card route
          setTransferInfo(null);
        },
      };

  const modalTitle = t('prepaidCardBalance', { cardAlias: t('cardAlias.bk') });

  return (
    <PaymentModalFocusTrap label={modalTitle}>
      <CardModal
        scrollView
        standardPadding
        analyticsEvent="Success transfer gift card"
        headerText={modalTitle}
        footer={
          <ActionButton
            fullWidth
            testID="confirm-now"
            onPress={actionHandler}
            isLoading={isLoading}
          >
            {actionText}
          </ActionButton>
        }
      >
        <GiftCardImage />
        <DatadogSessionReplayMask>
          <Typography testID="card-balance">
            {tRich('theCardBalanceIs', {
              cardNumber: <Typography bold>{formatCardNumber}</Typography>,
            })}
          </Typography>
        </DatadogSessionReplayMask>
        <CurrentBalanceText testID="transfer-balance">
          {formattedTransferCardBalance}
        </CurrentBalanceText>
        <ModalHr />
        <CurrentBalanceDescription>
          {t('yourBalanceIs', { currentBalance: formattedCurrecyBalance })}
        </CurrentBalanceDescription>
        <Typography>{actionDetails}</Typography>
      </CardModal>
    </PaymentModalFocusTrap>
  );
};

export const CurrentBalanceText = withTypography({
  fontSize: 'lg',
  bold: true,
  style: { marginTop: spacing.$2 },
});

export const ModalHr = Divider.withConfig({
  width: '100%',
  thickness: '1',
  marginX: 0,
  marginY: '$8',
});

export const CurrentBalanceDescription = withTypography({
  fontSize: 'sm',
  bold: true,
});
